import React, { useState } from 'react'
import { FaWhatsapp } from "react-icons/fa6";
import ContactBgImage from '../assets/images/contact-hero-handshake.jpeg'
import JCLocation from '../components/JCLocation';
import ToTop from '../components/ToTop';

const INQUIRY_TYPES = ['New Project', 'Partnership', 'Support', 'Other'];

const LIMITS = { name: 100, companyEmail: 254, industry: 100, message: 2000 };

/* Letters (any script), marks, spaces, hyphens, apostrophes and dots only, so
   markup or script tags can never be a "valid" name. */
const NAME_RE = /^[\p{L}\p{M}][\p{L}\p{M}'\-. ]*$/u;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

/* Trims first, so whitespace-only input is treated as empty. */
function validate(values) {
  const errors = {};

  const name = values.name.trim();
  if (!name) errors.name = 'Please enter your name.';
  else if (name.length > LIMITS.name) errors.name = `Please keep your name under ${LIMITS.name} characters.`;
  else if (!NAME_RE.test(name)) errors.name = 'Please use letters, spaces, hyphens and apostrophes only.';

  const email = values.companyEmail.trim();
  if (!email) errors.companyEmail = 'Please enter your company email address.';
  else if (email.length > LIMITS.companyEmail) errors.companyEmail = 'That email address is too long.';
  else if (!EMAIL_RE.test(email)) errors.companyEmail = 'Please enter a valid email address, e.g. name@company.com.';

  const industry = values.industry.trim();
  if (industry.length > LIMITS.industry) errors.industry = `Please keep this under ${LIMITS.industry} characters.`;

  const message = values.message.trim();
  if (!message) errors.message = 'Please tell us a little about what you need.';
  else if (message.length > LIMITS.message) errors.message = `Please keep your message under ${LIMITS.message} characters.`;

  return errors;
}

export default function Contact() {
  const [inquiryType, setInquiryType] = useState(INQUIRY_TYPES[0]);
  const [status, setStatus] = useState('idle');
  const [errors, setErrors] = useState({});

  function clearError(e) {
    const field = e.target.name;
    setErrors((prev) => (prev[field] ? { ...prev, [field]: undefined } : prev));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    const form = e.target;
    const data = new FormData(form);

    /* Honeypot: real users never see this field, bots fill it in. Formspree
       also drops anything with _gotcha set, this is the belt to that braces. */
    if (String(data.get('_gotcha') || '').trim()) return;

    const values = {
      name: String(data.get('name') || ''),
      companyEmail: String(data.get('companyEmail') || ''),
      industry: String(data.get('industry') || ''),
      message: String(data.get('message') || ''),
    };

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = form.elements[Object.keys(found)[0]];
      if (first) first.focus();
      return;
    }

    /* Send the trimmed values, not the raw ones. */
    Object.entries(values).forEach(([key, value]) => data.set(key, value.trim()));

    setStatus('submitting');
    try {
      const res = await fetch(form.action, {
        method: 'POST',
        body: data,
        headers: { Accept: 'application/json' },
      });
      setStatus(res.ok ? 'success' : 'error');
    } catch {
      setStatus('error');
    }
  }


  return (
    <div>
      {/* Hero section */}
      <div className="contact-hero-split">
        <img src={ContactBgImage} alt="Contact Jaggulas Consulting" className="contact-hero-split-img" />
        <div className="contact-hero-split-top-blend"></div>
        <div className="contact-hero-split-content">
          <h1>Let's solve your next challenge together.</h1>
        </div>
      </div>
      {/* Hero section ends*/}
      

      {/* Contact form section */}
      <div className='section-bg1 border-bottom border-secondary'>
      <div className="container">
        <div className="row g-5 d-flex justify-content-center">
          <div className="col-sm-8 p-5">
            <h2 className='pt-5 pb-2 contact-form-heading'>Every Great Project Starts with a Conversation</h2>
            <p className='contact-form-sub'>We'd love to hear about your business and how we can help.</p>

            <div className="contact-type-pills">
              {INQUIRY_TYPES.map((type) => (
                <button
                  key={type}
                  type="button"
                  className={"contact-type-pill" + (inquiryType === type ? " active" : "")}
                  onClick={() => setInquiryType(type)}
                >
                  {type}
                </button>
              ))}
            </div>

            {status === 'success' ? (
              <div className="contact-success" role="status">
                Thanks, we'll respond within 1 business day
              </div>
            ) : (
              <form id="contact-form" action="https://formspree.io/f/movqzqzj" method="POST" className='small py-4' onSubmit={handleSubmit} noValidate>
                <input type="hidden" name="inquiryType" value={inquiryType} />

                {/* Honeypot: hidden from people, tempting to bots. */}
                <input type="text" name="_gotcha" className="contact-hp" tabIndex="-1" autoComplete="off" aria-hidden="true" />

                <div className="row">
                  <div className='col-sm-6 py-2'>
                    <input
                      name="name"
                      type="text"
                      placeholder="Name*"
                      className={'input-full' + (errors.name ? ' input-invalid' : '')}
                      maxLength={LIMITS.name}
                      required
                      aria-invalid={errors.name ? 'true' : undefined}
                      aria-describedby={errors.name ? 'name-error' : undefined}
                      onInput={clearError}
                    />
                    {errors.name && <p className="contact-field-error" id="name-error">{errors.name}</p>}
                  </div>
                  <div className="col-sm-6 py-2">
                    <input
                      name="companyEmail"
                      id="userEmail"
                      type="email"
                      placeholder="Company Email Address*"
                      className={'input-full' + (errors.companyEmail ? ' input-invalid' : '')}
                      maxLength={LIMITS.companyEmail}
                      required
                      aria-invalid={errors.companyEmail ? 'true' : undefined}
                      aria-describedby={errors.companyEmail ? 'email-error' : undefined}
                      onInput={clearError}
                    />
                    {errors.companyEmail && <p className="contact-field-error" id="email-error">{errors.companyEmail}</p>}
                  </div>
                </div>

                <div className='py-2'>
                  <input
                    type="text"
                    name='industry'
                    placeholder='Industry'
                    className={'input-full' + (errors.industry ? ' input-invalid' : '')}
                    maxLength={LIMITS.industry}
                    aria-invalid={errors.industry ? 'true' : undefined}
                    aria-describedby={errors.industry ? 'industry-error' : undefined}
                    onInput={clearError}
                  />
                  {errors.industry && <p className="contact-field-error" id="industry-error">{errors.industry}</p>}
                </div>

                <textarea
                  name="message"
                  rows="2"
                  placeholder='What best describes your need?*'
                  className={errors.message ? 'input-invalid' : undefined}
                  maxLength={LIMITS.message}
                  required
                  aria-invalid={errors.message ? 'true' : undefined}
                  aria-describedby={errors.message ? 'message-error' : undefined}
                  onInput={clearError}
                ></textarea>
                {errors.message && <p className="contact-field-error" id="message-error">{errors.message}</p>}

                <div>
                  <button type="submit" className='know-more mt-3 py-2 px-3' disabled={status === 'submitting'}>
                    {status === 'submitting' ? 'Sending...' : 'Submit'}
                  </button>
                  <p className="contact-reply-note">We reply within 1 business day</p>
                  {status === 'error' && (
                    <p className="contact-error">Something went wrong. Please try again.</p>
                  )}
                </div>
            </form>
            )}


          </div>

          <div className="col-sm-4">
            <div className="book-call-card">
              <span className="book-call-eyebrow">Prefer to just talk?</span>
              <h4 className="book-call-heading">Book 20 minutes</h4>
              <p className="book-call-body">Grab a slot directly on our calendar, no waiting on a reply, talk to our team this week.</p>
              <a href="https://calendly.com/flamyjg-jaggulasconsulting" target="_blank" rel="noopener noreferrer" className="book-call-btn">Book a Call &rarr;</a>
            </div>

            <div className="whatsapp-card">
              <span className="whatsapp-card-eyebrow">Quick question?</span>
              <h4 className="whatsapp-card-heading">Chat on WhatsApp</h4>
              <p className="whatsapp-card-body">Message us directly, we're online most business hours.</p>
              <a href="https://wa.me/+27740687900?text=Hello! Jaggulas Consulting" target="_blank" rel="noopener noreferrer" className="whatsapp-card-btn">
                <FaWhatsapp /> Message us &rarr;
              </a>
            </div>
          </div>
        </div>
      </div>
      </div>


      {/* Contact form section ends */}

       {/* Location */}
        <JCLocation />
      {/* Location ends*/}

      {/* To top icon */}
        <ToTop />
      {/* To top icon */}





    </div>
  )
}
